import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Skeleton } from "@/components/Skeleton";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { useEffortUnit } from "@/lib/effort";
import { toCsv, downloadCsv } from "@/lib/taskTableViews";

function CapacityView() {
  const qc = useQueryClient();
  const unit = useEffortUnit();
  const me = useQuery({ queryKey: queryKeys.me(), queryFn: api.me });
  const isAdmin = me.data?.user?.role === "admin";
  const wl = useQuery({
    queryKey: queryKeys.teamWorkload(),
    queryFn: api.teamWorkload,
  });
  const timeOffQ = useQuery({ queryKey: ["time-off"], queryFn: api.timeOff });
  const [edit, setEdit] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: (v: { userId: string; capacity: number | null }) =>
      api.setUserCapacity(v.userId, v.capacity),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.teamWorkload() }),
  });

  const [offUser, setOffUser] = useState("");
  const [offStart, setOffStart] = useState("");
  const [offEnd, setOffEnd] = useState("");
  const addOff = useMutation({
    mutationFn: () =>
      api.addTimeOff({
        userId: offUser || undefined,
        startDate: offStart,
        endDate: offEnd,
      }),
    onSuccess: () => {
      setOffStart("");
      setOffEnd("");
      qc.invalidateQueries({ queryKey: ["time-off"] });
    },
  });

  const members = wl.data?.members ?? [];

  // Current Mon–Sun week boundary (local).
  const weekStart = new Date();
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7));
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d.toISOString().slice(0, 10);
  });

  // Days off in the current week for a member (from their time-off ranges).
  function daysOffThisWeek(userId: string): number {
    const ranges = (timeOffQ.data?.timeOff ?? []).filter(
      (t) => t.userId === userId,
    );
    return weekDates.filter((d) =>
      ranges.some((r) => d >= r.startDate && d <= r.endDate),
    ).length;
  }

  // Capacity reduced for non-working days this week.
  function effectiveCapacity(m: (typeof members)[number]): number | null {
    if (m.capacity == null) return null;
    const off = daysOffThisWeek(m.userId);
    return Math.round(m.capacity * (1 - off / 7) * 10) / 10;
  }

  function utilisation(m: (typeof members)[number]): number | null {
    const cap = effectiveCapacity(m);
    return cap && cap > 0 ? Math.round((m.committedEffort / cap) * 100) : null;
  }

  function exportCsv() {
    const rows = members.map((m) => [
      m.name,
      String(m.committedEffort),
      m.capacity != null ? String(m.capacity) : "",
      utilisation(m) != null ? `${utilisation(m)}%` : "",
    ]);
    downloadCsv(
      "capacity-utilisation.csv",
      toCsv(["member", "committed", "capacity", "utilisation"], rows),
    );
  }

  return (
    <div className="pg-wrap" style={{ padding: "22px 30px 60px" }}>
      <div className="rpt-head" style={{ marginBottom: 4 }}>
        <h1 className="pg-title" style={{ margin: 0 }}>
          Capacity &amp; utilisation
        </h1>
        <Button variant="secondary" onClick={exportCsv}>
          Export CSV
        </Button>
      </div>
      <p className="t-sm t-muted">
        Committed effort (open task estimates, in {unit}) vs each member&rsquo;s
        weekly capacity. Time off this week reduces effective capacity.
        Over-capacity (red) and under-utilised (&lt; 50%, blue) members are
        highlighted.
      </p>

      <div className="cap-timeoff-form">
        <span className="t-sm t-muted">Add time off:</span>
        <select
          className="select"
          style={{ width: 150 }}
          value={offUser}
          onChange={(e) => setOffUser(e.target.value)}
          aria-label="Time off member"
          disabled={!isAdmin}
          title={isAdmin ? "" : "Only admins can set time off for others"}
        >
          <option value="">{isAdmin ? "Me" : "Me"}</option>
          {isAdmin &&
            members.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name}
              </option>
            ))}
        </select>
        <input
          className="input"
          type="date"
          value={offStart}
          onChange={(e) => setOffStart(e.target.value)}
          aria-label="Time off start"
        />
        <input
          className="input"
          type="date"
          value={offEnd}
          onChange={(e) => setOffEnd(e.target.value)}
          aria-label="Time off end"
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={!offStart || !offEnd || addOff.isPending}
          onClick={() => addOff.mutate()}
        >
          Add
        </Button>
      </div>

      {wl.isLoading ? (
        <Skeleton count={5} height={48} />
      ) : (
        <ul className="cap-list">
          {members.map((m) => {
            const cap = effectiveCapacity(m);
            const off = daysOffThisWeek(m.userId);
            const over = cap != null && cap > 0 && m.committedEffort > cap;
            const util = utilisation(m);
            const under = util != null && util < 50;
            const pct =
              cap && cap > 0
                ? Math.min(100, Math.round((m.committedEffort / cap) * 100))
                : 0;
            return (
              <li key={m.userId} className="cap-row">
                <div className="cap-who">
                  <Avatar id={m.userId} name={m.name} size={28} />
                  <span>{m.name}</span>
                  {off > 0 && (
                    <span
                      className="t-xs"
                      style={{ color: "var(--accent, #2563eb)" }}
                      title="Days off this week reduce effective capacity"
                    >
                      ⛱ {off}d off
                    </span>
                  )}
                </div>
                <div className="cap-bar-wrap">
                  <div className="cap-bar">
                    <div
                      className={`cap-fill${over ? " over" : ""}${under ? " under" : ""}`}
                      style={{ width: `${cap ? pct : 0}%` }}
                    />
                  </div>
                  <span className="t-sm">
                    {m.committedEffort} / {cap ?? "—"} {unit}
                    {util != null && (
                      <span
                        className={`cap-util${over ? " over" : ""}${under ? " under" : ""}`}
                      >
                        {" "}
                        · {util}%
                      </span>
                    )}
                    {over && (
                      <span className="cap-over-tag">
                        {" "}
                        over by {(m.committedEffort - cap!).toFixed(0)}
                      </span>
                    )}
                  </span>
                </div>
                {isAdmin && (
                  <div className="cap-edit">
                    <input
                      className="input"
                      type="number"
                      min="0"
                      step="0.5"
                      style={{ width: 80 }}
                      placeholder="cap"
                      value={edit[m.userId] ?? (cap != null ? String(cap) : "")}
                      onChange={(e) =>
                        setEdit((s) => ({ ...s, [m.userId]: e.target.value }))
                      }
                      aria-label={`Capacity for ${m.name}`}
                    />
                    <button
                      type="button"
                      className="chip-btn"
                      disabled={save.isPending}
                      onClick={() => {
                        const v = edit[m.userId];
                        save.mutate({
                          userId: m.userId,
                          capacity:
                            v == null || v.trim() === "" ? null : Number(v),
                        });
                      }}
                    >
                      Set
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export const Route = createFileRoute("/capacity")({
  component: CapacityView,
});
